import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SummaryRow, SummaryColumns, ProductBacklogItem } from "@/types/capex";

interface MonthlySummaryState {
  summaryRows: SummaryRow[];
  selectedMonth: string;
  columns: SummaryColumns;
  expandedRows: string[];
  loading: boolean;
  error: string | null;
}

// Get current month in YYYY-M format
const getCurrentMonth = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${now.getMonth() + 1}`;
};

const initialState: MonthlySummaryState = {
  summaryRows: [],
  selectedMonth: getCurrentMonth(),
  columns: {
    assignedTo: { show: true, label: "Assigned To" },
    area: { show: true, label: "Area" },
  },
  expandedRows: [],
  loading: false,
  error: null,
};

export const monthlySummarySlice = createSlice({
  name: "monthlySummary",
  initialState,
  reducers: {
    setSummaryRows(state, action: PayloadAction<SummaryRow[]>) {
      state.summaryRows = action.payload;
    },
    setSelectedMonth(state, action: PayloadAction<string>) {
      state.selectedMonth = action.payload;
    },
    toggleColumn(state, action: PayloadAction<"assignedTo" | "area">) {
      const column = action.payload;
      const otherColumn = column === "assignedTo" ? "area" : "assignedTo";

      // Prevent hiding the last visible column
      if (state.columns[column].show && !state.columns[otherColumn].show) {
        return;
      }

      state.columns[column].show = !state.columns[column].show;
    },
    toggleExpandedRow(state, action: PayloadAction<string>) {
      const rowKey = action.payload;
      const index = state.expandedRows.indexOf(rowKey);

      if (index === -1) {
        state.expandedRows.push(rowKey);
      } else {
        state.expandedRows.splice(index, 1);
      }
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    resetState() {
      return initialState;
    },
  },
});

export default monthlySummarySlice.reducer;

export const {
  setSummaryRows,
  setSelectedMonth,
  toggleColumn,
  toggleExpandedRow,
  setLoading,
  setError,
  resetState,
} = monthlySummarySlice.actions;

// Utility function to process work items into summary rows
export const processWorkItemsToSummary = (
  workItems: ProductBacklogItem[],
): SummaryRow[] => {
  const summaryMap = new Map<string, SummaryRow>();

  for (const pbi of workItems) {
    for (const hour of pbi.hours) {
      const assignedTo = hour.assignedTo?.displayName || "Unassigned";
      const email = hour.assignedTo?.email || "";
      const area = pbi.area;
      const valueArea = pbi.valueArea;
      const key = `${assignedTo}|${area}`;

      if (!summaryMap.has(key)) {
        summaryMap.set(key, {
          key,
          assignedTo,
          email,
          area,
          valueAreas: {},
          total: 0,
        });
      }

      const row = summaryMap.get(key)!;
      row.valueAreas[valueArea] =
        (row.valueAreas[valueArea] || 0) + hour.hoursSpent;
      row.total += hour.hoursSpent;
    }
  }

  return Array.from(summaryMap.values()).sort((a, b) =>
    a.assignedTo.localeCompare(b.assignedTo),
  );
};
